import { useState } from 'react'
import { useParams } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { motion } from 'framer-motion'
import { Search, Edit, Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar'
import { Card, CardContent, CardHeader } from '@/components/ui/card'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog'
import { messagesApi, usersApi, Conversation, User } from '@/lib/api'
import { formatRelativeTime, getInitials, truncate } from '@/lib/utils'

export function MessagesPage() {
  const { conversationId } = useParams()
  const [showNewMessageModal, setShowNewMessageModal] = useState(false)

  const { data: conversationsData, isLoading } = useQuery({
    queryKey: ['conversations'],
    queryFn: () => messagesApi.getConversations(),
  })

  const conversations = conversationsData?.data?.data || []

  if (conversationId) {
    return <ConversationView conversationId={conversationId} />
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="font-display text-2xl font-bold">Messages</h1>
        <Button size="icon" variant="outline" onClick={() => setShowNewMessageModal(true)}>
          <Edit className="h-4 w-4" />
        </Button>
      </div>

      <div className="relative">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input placeholder="Search messages..." className="pl-9" />
      </div>

      {isLoading ? (
        <div className="flex justify-center py-8">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      ) : conversations.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <h3 className="font-display text-lg font-semibold">No messages yet</h3>
            <p className="mt-2 text-muted-foreground">
              Start a conversation with someone
            </p>
            <Button className="mt-4" onClick={() => setShowNewMessageModal(true)}>
              <Edit className="mr-2 h-4 w-4" />
              New Message
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-1">
          {conversations.map((conversation: Conversation, index: number) => (
            <motion.div
              key={conversation.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
            >
              <ConversationItem conversation={conversation} />
            </motion.div>
          ))}
        </div>
      )}

      <NewMessageModal open={showNewMessageModal} onOpenChange={setShowNewMessageModal} />
    </div>
  )
}

function NewMessageModal({ open, onOpenChange }: { open: boolean; onOpenChange: (open: boolean) => void }) {
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedUser, setSelectedUser] = useState<User | null>(null)
  const [message, setMessage] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const queryClient = useQueryClient()

  const { data: searchResults, isLoading: searchLoading } = useQuery({
    queryKey: ['userSearch', searchQuery],
    queryFn: () => usersApi.search(searchQuery),
    enabled: searchQuery.length >= 2,
  })

  const createConversation = useMutation({
    mutationFn: async () => {
      if (!selectedUser) throw new Error('No user selected')
      const conv = await messagesApi.createConversation([selectedUser.id])
      if (message.trim()) {
        await messagesApi.sendMessage(conv.data.id, message)
      }
      return conv
    },
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ['conversations'] })
      onOpenChange(false)
      setSearchQuery('')
      setSelectedUser(null)
      setMessage('')
      window.location.href = `/messages/${response.data.id}`
    },
    onSettled: () => {
      setIsSubmitting(false)
    },
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!selectedUser) return
    setIsSubmitting(true)
    createConversation.mutate()
  }

  const users = searchResults?.data?.data || []

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent onClose={() => onOpenChange(false)}>
        <DialogHeader>
          <DialogTitle className="font-display">New Message</DialogTitle>
          <DialogDescription>
            Search for someone to start a conversation with.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="mt-4 space-y-4">
          {!selectedUser ? (
            <div className="space-y-2">
              <label htmlFor="search" className="text-sm font-medium">
                To
              </label>
              <Input
                id="search"
                placeholder="Search by name or username..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              {searchLoading && (
                <div className="flex justify-center py-4">
                  <Loader2 className="h-6 w-6 animate-spin text-primary" />
                </div>
              )}
              {searchQuery.length >= 2 && !searchLoading && users.length === 0 && (
                <p className="text-sm text-muted-foreground text-center py-4">
                  No users found
                </p>
              )}
              {users.length > 0 && (
                <div className="max-h-48 overflow-y-auto border rounded-md">
                  {users.map((user: User) => (
                    <button
                      type="button"
                      key={user.id}
                      onClick={() => setSelectedUser(user)}
                      className="w-full flex items-center gap-3 p-3 hover:bg-muted text-left"
                    >
                      <Avatar className="h-8 w-8">
                        <AvatarImage src={user.avatarUrl} />
                        <AvatarFallback>{getInitials(user.displayName)}</AvatarFallback>
                      </Avatar>
                      <div>
                        <p className="text-sm font-medium">{user.displayName}</p>
                        <p className="text-xs text-muted-foreground">@{user.username}</p>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <>
              <div className="flex items-center gap-3 p-3 bg-muted rounded-md">
                <Avatar className="h-8 w-8">
                  <AvatarImage src={selectedUser.avatarUrl} />
                  <AvatarFallback>{getInitials(selectedUser.displayName)}</AvatarFallback>
                </Avatar>
                <div className="flex-1">
                  <p className="text-sm font-medium">{selectedUser.displayName}</p>
                  <p className="text-xs text-muted-foreground">@{selectedUser.username}</p>
                </div>
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  onClick={() => setSelectedUser(null)}
                >
                  Change
                </Button>
              </div>
              <div className="space-y-2">
                <label htmlFor="message" className="text-sm font-medium">
                  Message (optional)
                </label>
                <textarea
                  id="message"
                  placeholder="Write your first message..."
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  className="w-full min-h-[80px] rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring resize-none"
                />
              </div>
            </>
          )}
          <DialogFooter className="mt-6">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting || !selectedUser}>
              {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Start Conversation
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

function ConversationItem({ conversation }: { conversation: Conversation }) {
  const otherParticipant = conversation.participants[0]
  const displayName = conversation.name || otherParticipant?.displayName || 'Unknown'
  const avatarUrl = conversation.avatarUrl || otherParticipant?.avatarUrl

  return (
    <a
      href={`/messages/${conversation.id}`}
      className="flex items-center gap-3 rounded-lg p-3 transition-colors hover:bg-muted"
    >
      <div className="relative">
        <Avatar>
          <AvatarImage src={avatarUrl} />
          <AvatarFallback>{getInitials(displayName)}</AvatarFallback>
        </Avatar>
        {conversation.unreadCount > 0 && (
          <span className="absolute -right-1 -top-1 flex h-5 w-5 items-center justify-center rounded-full bg-primary text-xs text-primary-foreground">
            {conversation.unreadCount > 9 ? '9+' : conversation.unreadCount}
          </span>
        )}
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between">
          <p className="font-medium truncate">{displayName}</p>
          {conversation.lastMessage && (
            <span className="text-xs text-muted-foreground">
              {formatRelativeTime(conversation.lastMessage.createdAt)}
            </span>
          )}
        </div>
        {conversation.lastMessage && (
          <p className="text-sm text-muted-foreground truncate">
            {truncate(conversation.lastMessage.content, 40)}
          </p>
        )}
      </div>
    </a>
  )
}

function ConversationView({ conversationId }: { conversationId: string }) {
  return (
    <Card className="h-[calc(100vh-12rem)]">
      <CardHeader className="border-b">
        <div className="flex items-center gap-3">
          <Avatar>
            <AvatarFallback>U</AvatarFallback>
          </Avatar>
          <div>
            <p className="font-medium">User Name</p>
            <p className="text-xs text-muted-foreground">Online</p>
          </div>
        </div>
      </CardHeader>
      <CardContent className="flex h-full flex-col justify-center py-12 text-center">
        <p className="text-muted-foreground">Messages will appear here</p>
      </CardContent>
    </Card>
  )
}
